/** Angle (facet) hooks — facets 1c-2. Backend: api/facets.py (1c-1). */
import { useEffect, useMemo, useRef } from "react";
import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { useConcept } from "@/hooks/useConcepts";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import type { Concept, ConceptScope, ConceptVersion, FacetDetail, FacetSummary, ProjectQuestion } from "@/types/concept";

/** Ring flag + active angles (position order). `multi` (2+) is when angle UI shows. */
export function useAngles(conceptId: string | undefined) {
  const { data: me } = useCurrentUser();
  const { data: concept } = useConcept(conceptId);
  const enabled = !!me?.features?.facets;
  const list = concept?.facet_list;
  return useMemo(() => {
    const facets: FacetSummary[] = enabled ? (list ?? []) : [];
    return { enabled, facets, multi: facets.length >= 2 };
  }, [enabled, list]);
}

/** All facets incl. retired (restore menu). Ring-gated server-side. */
export function useFacetList(conceptId: string, enabled: boolean) {
  return useQuery<FacetDetail[]>({
    queryKey: ["concepts", conceptId, "facets"],
    queryFn: () => apiClient.get(`api/concepts/${conceptId}/facets`).json(),
    enabled,
  });
}

export const facetMutationKey = (conceptId: string) => ["facets", conceptId];

/** True while any facet op on this concept is in flight (actions serialize). */
export function useFacetBusy(conceptId: string): boolean {
  return useIsMutating({ mutationKey: facetMutationKey(conceptId) }) > 0;
}

type Ctx = { previous?: Concept };

/** Optimistic concept-cache write, rollback on error, invalidate on settle.
 * onSettled RETURNS the invalidation so isPending lasts until the refetch —
 * with useFacetBusy that keeps a second op from snapshotting a stale cache. */
function useFacetMutation<V>(
  conceptId: string,
  mutationFn: (v: V) => Promise<Concept>,
  optimistic: (c: Concept, v: V) => Concept,
  { papers = false }: { papers?: boolean } = {},
) {
  const qc = useQueryClient();
  const key = ["concepts", conceptId];
  return useMutation<Concept, Error, V, Ctx>({
    mutationKey: facetMutationKey(conceptId),
    // A shared mutationKey alone does NOT serialize in TanStack v5; scope does.
    scope: { id: `facets:${conceptId}` },
    mutationFn,
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Concept>(key);
      if (previous) qc.setQueryData<Concept>(key, optimistic(previous, v));
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(key, ctx.previous);
    },
    onSettled: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: key }),
        qc.invalidateQueries({ queryKey: ["concepts"], exact: true }),
        // The active angle set decides which angle_scores the reads return.
        ...(papers
          ? [qc.invalidateQueries({ queryKey: ["papers", conceptId] }),
             qc.invalidateQueries({ queryKey: ["concept-paper", conceptId] })]
          : []),
      ]),
  });
}

function withList(c: Concept, list: FacetSummary[]): Concept {
  return { ...c, facet_list: list.map((f, i) => ({ ...f, position: i })) };
}

function applyPq(c: Concept, pq?: ProjectQuestion): Concept {
  return pq && pq.choice !== "keep" ? { ...c, abstract: pq.abstract, keywords: pq.keywords } : c;
}

/** Server rule (facets._mirror_text): at 1 active angle its text IS the project question. */
function mirrorIfSingle(c: Concept): Concept {
  const list = c.facet_list ?? [];
  if (list.length !== 1) return c;
  return { ...c, facet_list: [{ ...list[0], question: c.abstract, search_terms: c.keywords }] };
}

function definedOnly<T extends object>(o: T): Partial<T> {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as Partial<T>;
}

export interface FacetEdit {
  facetId: string;
  label?: string;
  question?: string;
  search_terms?: string;
  projectQuestion?: ProjectQuestion;
}

export function useUpdateFacet(conceptId: string) {
  return useFacetMutation<FacetEdit>(
    conceptId,
    ({ facetId, projectQuestion, ...fields }) =>
      apiClient
        .patch(`api/concepts/${conceptId}/facets/${facetId}`, {
          json: { ...definedOnly(fields), ...(projectQuestion ? { project_question: projectQuestion } : {}) },
        })
        .json<Concept>(),
    (c, { facetId, projectQuestion, ...fields }) => {
      const defined = definedOnly(fields);
      return {
        ...applyPq(c, projectQuestion),
        facet_list: (c.facet_list ?? []).map((f) => (f.id === facetId ? { ...f, ...defined } : f)),
      };
    },
  );
}

export interface FacetCreateVars {
  label: string;
  question: string;
  search_terms: string;
  scope?: ConceptScope | null;
  auto_drafted?: boolean;
  source: "add_angle" | "coverage_audit" | "from_paper";
  projectQuestion?: ProjectQuestion;
}

/** Add an angle (facets 1c-3). Placeholder row until the refetch lands. */
export function useCreateFacet(conceptId: string) {
  return useFacetMutation<FacetCreateVars>(
    conceptId,
    ({ projectQuestion, ...body }) =>
      apiClient
        .post(`api/concepts/${conceptId}/facets`, {
          json: { ...body, ...(projectQuestion ? { project_question: projectQuestion } : {}) },
          // The project-question rewrite rides along; a ky default timeout would turn a slow
          // success into an ambiguous failure (useAddAngle then adopts rather than re-creates).
          timeout: 120_000,
        })
        .json<Concept>(),
    (c, { projectQuestion, label, question, search_terms, scope }) =>
      withList(applyPq(c, projectQuestion), [
        ...(c.facet_list ?? []),
        { id: `pending-${label}`, position: 0, label, question, search_terms, scope: scope ?? null },
      ]),
    { papers: true },
  );
}

export interface FacetLifecycleVars { facetId: string; projectQuestion?: ProjectQuestion }

const lifecycleBody = (pq?: ProjectQuestion) => ({ json: pq ? { project_question: pq } : {} });

export function useRetireFacet(conceptId: string) {
  return useFacetMutation<FacetLifecycleVars>(
    conceptId,
    ({ facetId, projectQuestion }) =>
      apiClient.post(`api/concepts/${conceptId}/facets/${facetId}/retire`, lifecycleBody(projectQuestion)).json<Concept>(),
    (c, { facetId, projectQuestion }) =>
      mirrorIfSingle(withList(applyPq(c, projectQuestion), (c.facet_list ?? []).filter((f) => f.id !== facetId))),
    { papers: true },
  );
}

/** `facet` is the retired FacetDetail from useFacetList (optimistic append). */
export function useRestoreFacet(conceptId: string) {
  return useFacetMutation<FacetLifecycleVars & { facet: FacetSummary }>(
    conceptId,
    ({ facetId, projectQuestion }) =>
      apiClient.post(`api/concepts/${conceptId}/facets/${facetId}/restore`, lifecycleBody(projectQuestion)).json<Concept>(),
    (c, { facet, projectQuestion }) => {
      const { id, position, label, question, search_terms, scope } = facet;
      return withList(applyPq(c, projectQuestion), [...(c.facet_list ?? []), { id, position, label, question, search_terms, scope }]);
    },
    { papers: true },
  );
}

export function useReorderFacets(conceptId: string) {
  return useFacetMutation<string[]>(
    conceptId,
    (facetIds) => apiClient.put(`api/concepts/${conceptId}/facets/order`, { json: { facet_ids: facetIds } }).json<Concept>(),
    (c, facetIds) => {
      const byId = new Map((c.facet_list ?? []).map((f) => [f.id, f]));
      return withList(c, facetIds.flatMap((id) => (byId.has(id) ? [byId.get(id)!] : [])));
    },
  );
}

/** Re-queue the angle pass after retries were exhausted. */
export function useRetryAngleRescore(conceptId: string) {
  return useFacetMutation<void>(
    conceptId,
    () => apiClient.post(`api/concepts/${conceptId}/facets/rescore`).json<Concept>(),
    (c) => ({ ...c, angle_rescore_failed: false, angle_rescore_pending: true }),
  );
}

/** Restore a version with its facets (ring-gated endpoint). */
export function useRestoreVersion(conceptId: string) {
  return useFacetMutation<ConceptVersion>(
    conceptId,
    (v) => apiClient.post(`api/concepts/${conceptId}/history/${v.id}/restore`).json<Concept>(),
    (c, v) => {
      const next = { ...c, name: v.name, abstract: v.abstract, keywords: v.keywords };
      const primary = (c.facet_list ?? [])[0];
      if (!v.facets) {
        // Pre-085 snapshot: the primary facet alone, from the version's text (facets.py restore_version).
        return primary
          ? withList(next, [{ ...primary, label: v.name, question: v.abstract, search_terms: v.keywords }])
          : next;
      }
      const active = v.facets
        .filter((f) => !f.retired_at && f.id)
        .sort((a, b) => a.position - b.position)
        .map((f) => ({ id: f.id as string, position: f.position, label: f.label, question: f.question,
                       search_terms: f.search_terms, scope: f.scope }));
      // Server (facets.py restore_version): a single active facet mirrors the
      // version's text but keeps the snapshot's own label (blank -> the name).
      if (active.length === 1) {
        return withList(next, [{ ...active[0], label: active[0].label || v.name, question: v.abstract, search_terms: v.keywords }]);
      }
      return withList(next, active);
    },
    { papers: true },
  );
}

export interface AngleText { label: string; question: string }

/** LLM redraft of the project question over a proposed angle set. Persists nothing. */
export function useDraftProjectQuestion(conceptId: string) {
  return useMutation<{ abstract: string; keywords: string }, Error, AngleText[]>({
    mutationFn: (angles) =>
      apiClient
        .post(`api/concepts/${conceptId}/facets/project-question/draft`, { json: { angles }, timeout: 120_000 })
        .json(),
  });
}

/** When the background angle pass finishes (pending true -> false), refetch
 * papers so new angle scores show without navigating. */
export function useAngleRescoreRefresh(conceptId: string, pending: boolean | undefined) {
  const qc = useQueryClient();
  const prev = useRef<{ id: string; pending: boolean | undefined }>({ id: conceptId, pending });
  useEffect(() => {
    if (prev.current.id === conceptId && prev.current.pending && !pending) {
      qc.invalidateQueries({ queryKey: ["papers", conceptId] });
      qc.invalidateQueries({ queryKey: ["concept-paper", conceptId] });
    }
    prev.current = { id: conceptId, pending };
  }, [pending, conceptId, qc]);
}
